// The one-time offer to keep the catalogue on the device, and the nudge when a newer one exists.
// A dismissible strip built from the Button atoms; it shows only on the
// landing screens, never over the camera or a note in progress.
import { useState } from "react";
import { formatNumber } from "../lib/format";
import { useT } from "../lib/i18n";
import {
  answerFirstPrompt,
  declineUpdate,
  downloadCatalogue,
  useCataloguePrompt,
  useCatalogueState,
} from "../lib/catalogue";
import { useRoute } from "../lib/router";
import { Body, Button, Card, Column, Fill, Row } from "../ui";

const BYTES_PER_MB = 1_000_000;
/** The dismissible strip over the top of a landing screen, same look as the update prompt. */
function Strip({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <Column
      position="fixed"
      top="3"
      left={0}
      right={0}
      zIndex="5"
      alignItems="center"
      paddingHorizontal="4"
      pointerEvents="none"
    >
      <Card
        role="status"
        data-testid="catalogue-prompt"
        width="100%"
        maxWidth={600}
        pointerEvents="auto"
        boxShadow="var(--shSheet)"
      >
        {children}
      </Card>
    </Column>
  );
}

const LANDING = new Set(["", "taste", "search"]);

export function CataloguePrompt(): React.JSX.Element | null {
  const { t, locale } = useT();
  const kind = useCataloguePrompt();
  const { offer, task, error, freeBytes } = useCatalogueState();
  const route = useRoute();
  // Tapped Download without the room for it: the strip stays and says so, the question unanswered.
  const [noRoom, setNoRoom] = useState(false);
  const megabytes = (bytes: number): string =>
    `${formatNumber(locale, bytes / BYTES_PER_MB, bytes >= 100 * BYTES_PER_MB ? 0 : 1)} MB`;
  const working = task?.kind === "downloading";
  const percent =
    task?.kind === "downloading" && task.progress !== undefined && task.progress.total > 0
      ? Math.floor((task.progress.loaded / task.progress.total) * 100)
      : 0;
  // A download nobody was asked about (a first launch, or a page waiting for its data) is told
  // wherever the person is, as progress only; there is no question to put to them.
  if (working && (kind === undefined || offer === undefined)) {
    return (
      <Strip>
        <Body>{t("about.catalogue.downloading", { percent })}</Body>
      </Strip>
    );
  }
  if (kind === undefined || offer === undefined) return null;
  if (!LANDING.has(route.segments[0] ?? "") || route.segments.length > 1) return null;

  const size = megabytes(offer.bytes);
  const accept = (): void => {
    if (freeBytes !== undefined && freeBytes < offer.diskBytes) {
      setNoRoom(true);
      return;
    }
    if (kind === "first") answerFirstPrompt();
    void downloadCatalogue();
  };
  const decline = (): void => {
    if (kind === "first") answerFirstPrompt();
    else declineUpdate(offer.version);
  };

  return (
    <Strip>
      <Row wrap spread gap="3">
        <Fill minWidth={200}>
          <Body>
            {working
              ? t("about.catalogue.downloading", { percent })
              : t(kind === "first" ? "catalogue.prompt.first" : "catalogue.prompt.update", {
                  size,
                })}
            {/* Size against room, so the choice is made knowing whether it fits. */}
            {!working && freeBytes !== undefined && (
              <> · {t("catalogue.prompt.space", { free: megabytes(freeBytes) })}</>
            )}
          </Body>
        </Fill>
        {!working && (
          <Row gap="2">
            <Button tone="primary" onPress={accept} data-testid="catalogue-prompt-accept">
              {t(kind === "first" ? "catalogue.prompt.download" : "catalogue.prompt.update.action")}
            </Button>
            <Button tone="ghost" onPress={decline} data-testid="catalogue-prompt-later">
              {t("catalogue.prompt.later")}
            </Button>
          </Row>
        )}
      </Row>
      {(noRoom || error === "no-space") && (
        <Body tone="error" role="alert">
          {t("about.catalogue.error.no-space")}
        </Body>
      )}
    </Strip>
  );
}
